import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useScheduleRunSessionQuery } from 'src/api/queries/schedulesQueries';
import { useAiSchedulesUnavailable } from 'src/hooks/useAiSchedulesUnavailable';
import { FeatureUnavailable } from 'src/components';
import { AssistantMessage } from 'src/chat/AssistantMessage';
import { UserMessage } from 'src/chat/UserMessage';
import { adaptScheduleRunMessages } from 'src/chat/scheduleRunAdapter';
import styles from './ScheduleRunReplay.module.scss';

export const ScheduleRunReplay: React.FC = () => {
  const history = useHistory();
  const { id, sessionId } = useParams<{ id: string; sessionId: string }>();
  const { data, isLoading, error } = useScheduleRunSessionQuery(sessionId);
  const scheduleFeatureUnavailable = useAiSchedulesUnavailable();

  const adapted = React.useMemo(
    () => (data ? adaptScheduleRunMessages(data) : []),
    [data],
  );

  if (scheduleFeatureUnavailable) {
    return <FeatureUnavailable title="Scheduled agents" />;
  }

  if (isLoading) {
    return <div className={styles.page}><div className={styles.loading}>Loading run transcript…</div></div>;
  }

  if ((error as any)?.status === 404 || !data) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <h2>Run not found</h2>
          <p>This run may have been pruned (older than the last 50 runs).</p>
          <button className={styles.backBtn} onClick={() => history.push(`/ai/schedules/${id}`)}>
            ← Back to schedule
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => history.push(`/ai/schedules/${id}`)}>
          ← Back to schedule
        </button>
        <div className={styles.banner}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="15.5" cy="16.5" r="4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M15.5 15v2l1.5 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Scheduled run</span>
          <span className={styles.sessionId}>{data.id}</span>
        </div>
      </div>

      <div className={styles.transcript}>
        {adapted.map((item, idx) => (
          <React.Fragment key={item.message.id}>
            {item.message.role === 'user' ? (
              <UserMessage message={item.message} />
            ) : (
              <AssistantMessage message={item.message} isLast={idx === adapted.length - 1} />
            )}
            {item.legacyToolSummaries && item.legacyToolSummaries.length > 0 && (
              <ul className={styles.toolSummaries}>
                {item.legacyToolSummaries.map((summary, i) => (
                  <li key={i}>{summary}</li>
                ))}
              </ul>
            )}
          </React.Fragment>
        ))}
        {adapted.length === 0 && (
          <div className={styles.emptyTranscript}>No messages in this run.</div>
        )}
      </div>
    </div>
  );
};
